import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AuthImage } from './AuthImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ArtAIGenerateModal — generate AI suggestions for art props/set dressing/vehicles
 * OR costume items, depending on `department` prop.
 *
 * Props: { open, onClose, projectId, sceneId, allScenes, token, onAssetAdded, initialPrompt, department }
 *  - department: "ART" (default) | "COSTUME" — swaps endpoint + title + placeholder
 *  - sceneId optional. When omitted, a scene picker is shown.
 *  - initialPrompt optional pre-filled prompt (used by the "+ Generate similar" affordance).
 */
export default function ArtAIGenerateModal({
  open, onClose, projectId, sceneId, allScenes = [], token, onAssetAdded, initialPrompt = '',
  department = 'ART',
}) {
  const isCostume = department === 'COSTUME';
  const apiBase = isCostume ? 'costume-assets' : 'art-assets';
  const modalTitle = isCostume ? 'AI Suggest Costumes' : 'AI Generate Props';
  const promptPlaceholder = isCostume
    ? 'e.g., royal Kerala court attire, casual urban streetwear, period 1960s Malabar...'
    : 'e.g., vintage kitchen props, brass utensils, Kerala traditional...';
  const testId = isCostume ? 'costume-ai-generate-modal' : 'art-ai-generate-modal';
  const submitTestId = isCostume ? 'costume-ai-generate-submit' : 'art-ai-generate-submit';
  const itemTestPrefix = isCostume ? 'costume-suggestion-add-btn-' : 'art-suggestion-add-btn-';

  const [prompt, setPrompt] = useState(initialPrompt);
  const [count, setCount] = useState(3);
  const [pickedSceneId, setPickedSceneId] = useState(sceneId || '');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [added, setAdded] = useState({}); // {idx: true}

  // Re-sync when the modal is reopened with a different initialPrompt or sceneId.
  React.useEffect(() => {
    if (open) {
      setPrompt(initialPrompt || '');
      setPickedSceneId(sceneId || '');
      setSuggestions([]); setAdded({});
    }
  }, [open, initialPrompt, sceneId]);

  const headers = { Authorization: `Bearer ${token}` };
  const sceneForAdd = sceneId || pickedSceneId;
  const sceneChip = sceneId
    ? allScenes.find(s => s.id === sceneId)
    : null;

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    setLoading(true); setSuggestions([]); setAdded({});
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/${apiBase}/ai-suggest`,
        { prompt, count, scene_id: sceneForAdd || undefined },
        { headers }
      );
      setSuggestions(data.suggestions || []);
      if (!(data.suggestions || []).length) toast.info('No suggestions returned.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 || detail?.error === 'LLM_BUDGET_EXCEEDED') {
        toast.error('AI generation limit reached. Please try again later.');
      } else {
        toast.error(detail?.message || detail || 'Generation failed');
      }
    } finally { setLoading(false); }
  };

  const handleAddOne = async (idx) => {
    const s = suggestions[idx];
    if (!sceneForAdd) { toast.error('Pick a scene first'); return; }
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/${apiBase}/add-from-suggestion`,
        { scene_id: sceneForAdd, name: s.name, description: s.description,
          sub_type: s.sub_type, image_path: s.image_path },
        { headers }
      );
      setAdded(p => ({ ...p, [idx]: true }));
      toast.success(`Added "${s.name}"`);
      onAssetAdded?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add');
    }
  };

  const handleAddAll = async () => {
    if (!sceneForAdd) { toast.error('Pick a scene first'); return; }
    for (let i = 0; i < suggestions.length; i++) {
      if (!added[i]) {
        // eslint-disable-next-line no-await-in-loop
        await handleAddOne(i);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] max-w-3xl"
        data-testid={testId}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[var(--cb-accent)]" /> {modalTitle}
          </DialogTitle>
          <DialogDescription className="text-xs text-[var(--cb-text-sec)]">
            Each generation uses AI credits from your account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {sceneChip ? (
            <div className="text-xs text-[var(--cb-text-sec)]">
              Scene:{' '}
              <span className="px-2 py-0.5 rounded bg-[var(--cb-accent)]/15 text-[var(--cb-accent)] font-semibold">
                SC.{sceneChip.scene_number ?? '—'} · {sceneChip.scene_header}
              </span>
            </div>
          ) : (
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Scene (optional)</label>
              <Select value={pickedSceneId} onValueChange={setPickedSceneId}>
                <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]">
                  <SelectValue placeholder="No scene (general)" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] max-h-72">
                  {allScenes.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-[var(--cb-text)]">
                      SC.{s.scene_number ?? '—'} · {s.scene_header}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Prompt *</label>
            <Textarea
              value={prompt} onChange={e => setPrompt(e.target.value)}
              placeholder={promptPlaceholder}
              className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] min-h-[80px]"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs text-[var(--cb-text-sec)]">Count</label>
            <Select value={String(count)} onValueChange={v => setCount(Number(v))}>
              <SelectTrigger className="w-24 bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                {[1, 2, 3, 4, 5].map(n => (
                  <SelectItem key={n} value={String(n)} className="text-[var(--cb-text)]">{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto">
              <Button onClick={handleGenerate} disabled={loading}
                className="bg-[var(--cb-accent)] hover:opacity-90 text-black font-semibold"
                data-testid={submitTestId}>
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : 'Generate'}
              </Button>
            </div>
          </div>

          {suggestions.length > 0 && (
            <div className="pt-3 border-t border-[var(--cb-border)]">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--cb-text-sec)]">
                  Suggestions ({suggestions.length})
                </p>
                <Button size="sm" variant="outline" onClick={handleAddAll}
                  className="border-[var(--cb-border)] text-[var(--cb-text)]">
                  Add all
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {suggestions.map((s, i) => (
                  <div key={i} className="rounded-lg border border-[var(--cb-border)] bg-[var(--cb-bg)] p-3 flex flex-col">
                    <div className="aspect-square rounded mb-2 overflow-hidden bg-[var(--cb-border)] flex items-center justify-center">
                      {s.image_path ? (
                        <AuthImage path={s.image_path} alt={s.name}
                          className="w-full h-full object-cover" />
                      ) : (
                        <Sparkles className="h-8 w-8 text-[var(--cb-text-sec)] opacity-30" />
                      )}
                    </div>
                    <div className="text-sm font-semibold text-[var(--cb-text)] mb-0.5">{s.name}</div>
                    <div className="text-xs text-[var(--cb-text-sec)] mb-2 flex-grow">{s.description}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--cb-accent)]/10 text-[var(--cb-accent)]">
                        {s.sub_type}
                      </span>
                      <Button size="sm"
                        disabled={added[i] || !sceneForAdd}
                        onClick={() => handleAddOne(i)}
                        className={added[i]
                          ? 'bg-green-600/20 text-green-400 border border-green-600/40 hover:bg-green-600/20'
                          : 'bg-[var(--cb-accent)] hover:opacity-90 text-black font-semibold'}
                        data-testid={`${itemTestPrefix}${i}`}>
                        {added[i] ? <><Check className="h-3 w-3 mr-1" /> Added</> : '+ Add to list'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
